"use client";

import LoadingText from "@/components/common/loading-text";
import { formatPrice } from "@/lib/utils";
import { IGetJoinCommunityStatusResponseDTO } from "@/types/community";
import { PlusIcon } from "lucide-react";
import CustomPaymentLinkAlertDialog from "./custom-payment-link-alert-dialog";

interface JoinChannelButtonProps {
	onClick: () => void;
	isLoading: boolean;
	paymentSettings: IGetJoinCommunityStatusResponseDTO["data"]["settings"];
}

const JoinChannelButton = ({ onClick, isLoading, paymentSettings }: JoinChannelButtonProps) => {
	const isPaid = paymentSettings?.isPaid || false;
	const isSubscription = paymentSettings?.paymentType === "SUBSCRIPTION";
	const regularPrice = paymentSettings?.price || 0;
	const discountedPrice = regularPrice - (paymentSettings?.discount || 0);

	const onCustomPaymentLinkClick = () => {
		window.open(paymentSettings?.customPaymentLink, "_blank");
	};

	const renderPrice = () => {
		if (discountedPrice) {
			return (
				<div className="flex items-center space-x-1">
					<span className="text-lg font-bold">{formatPrice(discountedPrice, "USD")}</span>

					<span className="text-xs text-blue-300 line-through">
						{formatPrice(regularPrice, "USD")}
					</span>
				</div>
			);
		}
		return <span className="text-lg font-bold">{formatPrice(regularPrice, "USD")}</span>;
	};

	const renderPeriod = () => {
		if (isSubscription) return <span className="font-medium">/ mo</span>;

		return null;
	};

	// Custom payment link
	if (paymentSettings?.customPaymentLink) {
		return (
			<CustomPaymentLinkAlertDialog
				price={renderPrice()}
				period={renderPeriod()}
				onConfirm={onCustomPaymentLinkClick}
			/>
		);
	}

	// Paid channel join button
	if (isPaid) {
		return (
			<button
				type="button"
				onClick={onClick}
				disabled={isLoading}
				className="flex w-fit items-center justify-between gap-3 rounded-md bg-brand-primary px-4 py-2 font-medium text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 disabled:opacity-50"
			>
				{isLoading ? (
					<LoadingText classNames={{ loader: "stroke-white" }} />
				) : (
					<>
						<span className="text-white/90 transition-colors duration-300 group-hover:text-white">
							Join now
						</span>

						<div className="flex items-center space-x-1">
							{renderPrice()}
							{renderPeriod()}
						</div>
					</>
				)}
			</button>
		);
	}

	// Free channel join button
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={isLoading}
			className="flex w-full items-center justify-center gap-1 rounded-md bg-brand-primary px-4 py-2 font-medium text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 disabled:opacity-50 md:w-44"
		>
			<div className="flex items-center justify-between gap-2">
				<span>Join</span>
				<PlusIcon className="h-5 w-5" />
			</div>
		</button>
	);
};

export default JoinChannelButton;
